import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { undoLastChange } from "@/features/shell/actions";
import {
  selectHasUnsavedConfiguration,
  selectHistoryChanges,
  selectPlanValue,
} from "@/features/shell/legacySelectors";
import { selectSaveRequestId } from "@/features/shell/slice";
import { Save, Undo2 } from "@/shared/ui/icons";
import { ChangeVersionModal } from "./ChangeVersionModal";
import { LayerVersionPicker } from "./LayerVersionPicker";
import { PreviewButton } from "./PreviewButton";
import { PublishToggle } from "./PublishToggle";
import { useLayerVersions } from "./useLayerVersions";
import { useSaveConfiguration } from "./useSaveConfiguration";

/** Barra della topbar: versione del layer, pubblicazione, anteprima e azioni di salvataggio (SaveChanges.jsx). */
export function SaveChangesBar() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useAppSelector(selectHistoryChanges);
  const hasRealChanges = useAppSelector(selectHasUnsavedConfiguration);
  const planActive = Boolean(useAppSelector(selectPlanValue).isActive);
  const saveRequestId = useAppSelector(selectSaveRequestId);
  const { versions, current, versionChanged, selectVersion, markSaved } = useLayerVersions();
  const { save } = useSaveConfiguration();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [versionModal, setVersionModal] = useState(false);

  const hasChanges = history.length > 0;
  // SaveChanges.disabled: con history vuota il bottone e' abilitato (ma non visibile), altrimenti solo con modifiche reali
  const saveDisabled = hasChanges && !hasRealChanges;

  // Ritorna true se il salvataggio e' andato a buon fine: la modale di conferma versione si chiude solo in quel caso
  // (il legacy `await vm.saveChanges(); vm.versionModal = false` restava aperto su una PUT rifiutata).
  const saveNow = async (): Promise<boolean> => {
    try {
      await save();
      markSaved();
      return true;
    } catch (error) {
      // Il legacy lasciava la promessa rifiutata senza feedback: il toast di errore sulle mutation arriva
      // con il middleware della spec 7.6 (fase 3). Qui si evita solo l'unhandled rejection.
      console.error("Salvataggio della configurazione fallito:", error);
      return false;
    }
  };
  const handleSaveChanges = () => {
    if (versionChanged) setVersionModal(true);
    else void saveNow();
  };

  // saveNow cambia identita' a ogni render (chiude su save/markSaved aggiornati): tenerlo in una ref aggiornata
  // in un effetto, cosi' l'effetto sotto puo' chiamare la versione corrente senza rientrare in
  // `react-hooks/exhaustive-deps` ne' rieseguirsi a ogni render.
  const saveNowRef = useRef(saveNow);
  useEffect(() => {
    saveNowRef.current = saveNow;
  });

  // Comando `save-changes` dalle viste legacy: salva subito, senza la conferma di versione (SaveChanges.saveChanges).
  // Un ref (non uno stato) tiene traccia dell'ultimo comando gestito: un render scartato (es. una vista lazy che
  // sospende sopra questo componente) non fa perdere l'informazione e non fa scattare un secondo salvataggio,
  // a differenza del pattern "stato durante il render" con `queueMicrotask` (fallback del brief, rivisto qui).
  const handledRequest = useRef(saveRequestId);
  useEffect(() => {
    if (handledRequest.current === saveRequestId) return;
    handledRequest.current = saveRequestId;
    void saveNowRef.current();
  }, [saveRequestId]);

  if (!current) return null;

  return (
    <div className={`save-changes ${hasChanges ? "with-changes" : ""} ${planActive ? "" : "locked"}`}>
      <div className="save-primary-controls">
        <LayerVersionPicker
          versions={versions}
          current={current}
          changed={versionChanged}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onSelect={(version) => {
            selectVersion(version);
            setPickerOpen(false);
          }}
        />
        {!hasChanges && <PublishToggle />}
        <PreviewButton />
      </div>
      {hasChanges && (
        <div className="save-actions" role="status">
          <span className="save-actions-label">
            <span className="size-1.5 rounded-full bg-warning" />
            {t("saveChanges.pendingChanges", { var1: history.length })}
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm h-9 gap-1.5 text-secondary"
            onClick={() => dispatch(undoLastChange())}
          >
            <Undo2 className="size-4" aria-hidden="true" />
            {t("saveChanges.discardChanges")}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm h-9 gap-1.5 shadow-none"
            disabled={saveDisabled}
            onClick={handleSaveChanges}
          >
            <Save className="size-4" aria-hidden="true" />
            {t("saveChanges.saveChanges")}
          </button>
        </div>
      )}
      <ChangeVersionModal
        open={versionModal}
        versionName={current.versionName}
        onCancel={() => setVersionModal(false)}
        onConfirm={() => {
          void saveNow().then((ok) => {
            if (ok) setVersionModal(false);
          });
        }}
      />
    </div>
  );
}
